import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import IconButton from 'fe-design-base/molecules/IconButton';

import PurchaseMethodButton from 'features/biller/implementations/stripe/PurchaseMethodModule/PurchaseMethodButton';
import { Price } from 'features/biller/implementations/stripe/PurchaseMethodModule/PurchaseMethodModuleView';
import { fetchMarketingDiscountCoupon } from 'features/managePlan/actions';
import { CheckoutInitiatedFrom } from 'features/productGrowth/types';

import Drawer from 'components/Drawer';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { useTrackUx, withUxRoot } from 'util/uxEvents';

import { useAutoApplyDiscount } from './hooks';
import { getCheckout } from './selectors';
import { checkoutDismissed, checkoutSuccess, resetCheckout } from './slice';
import { Checkout, Success } from './views';

interface CheckoutMapping {
  title: string;
  subtitle: string;
  buttonLabel?: string;
  success_view: React.ComponentType;
  subscriptionType: 'training_assistant';
  tiers?: Record<any, any>;
}

const CHECKOUT_MAPPING: Record<CheckoutInitiatedFrom, CheckoutMapping> = {
  TRAINING_ASSISTANT: {
    title: toI18n('checkout_drawer.training_assistant.header'),
    subtitle: toI18n('checkout_drawer.training_assistant.subheader'),
    success_view: Success,
    subscriptionType: 'training_assistant',
  },
};

const { cx } = cxHelpers('CheckoutDrawer');

const CheckoutDrawer = () => {
  const dispatch = useDispatch();
  const trackUxEvent = useTrackUx();

  const [isLoading, setIsLoading] = useState(false);

  const { status, productInfo, isOpen, initiatedFrom, onSuccess } =
    useSelector(getCheckout);

  const defaultPrice: Price = {
    amountInCents: 0,
    priceSystemId: '',
  };

  const [selectedPrice, setSelectedPrice] = useState(defaultPrice);
  const [selectedProductUuid, setSelectedProductUuid] = useState<
    string | null | undefined
  >(null);

  useEffect(() => {
    const firstPrice = productInfo?.[0].price;

    if (!firstPrice) return;

    if (productInfo.length === 1) {
      setSelectedPrice({
        amountInCents: firstPrice[0].amount_in_cents,
        priceSystemId: firstPrice[0].system_id,
      });
      setSelectedProductUuid((productInfo[0] as any)?.product_uuid || null);
    }
  }, [productInfo]);

  // Get Purchase Abandoned event data for Task Manager checkout
  const getPurchaseAbandonedEventData = useCallback(
    (closingWindow: boolean) => ({
      selected_plan: null, // No plan selection in add-on only checkout
      selected_billing_frequency: null, // No billing frequency for add-ons
      selected_add_on: [initiatedFrom?.toLowerCase()],
      promo_code: null, // No promo code support in current implementation
      closing_window: closingWindow,
    }),
    [initiatedFrom]
  );

  const handleClose = useCallback(() => {
    if (status === 'success') {
      trackUxEvent(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
        element: 'close_button',
        stage: 'checkout_completed',
      });
      dispatch(resetCheckout());
    } else {
      // Track Purchase Abandoned event
      trackUxEvent(
        'Purchase Abandoned',
        TRACK_ACTION_TYPES.CLICK,
        getPurchaseAbandonedEventData(false)
      );
      dispatch(checkoutDismissed());
    }
  }, [dispatch, status, trackUxEvent, getPurchaseAbandonedEventData]);

  useAutoApplyDiscount({
    subscriptionType: initiatedFrom?.toLowerCase() || '',
    productUuid: selectedProductUuid,
  });

  useEffect(() => {
    dispatch(fetchMarketingDiscountCoupon());
  }, [dispatch]);

  // Add beforeunload event listener to detect window/tab closing
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Track Purchase Abandoned event when closing window/tab
      if (status === 'success') {
        trackUxEvent(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
          element: 'close_button',
          stage: 'checkout_completed',
        });
      } else {
        trackUxEvent(
          'Purchase Abandoned',
          TRACK_ACTION_TYPES.CLICK,
          getPurchaseAbandonedEventData(true)
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [trackUxEvent, getPurchaseAbandonedEventData, status]);

  const handleOnLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const handleSuccess = useCallback(async () => {
    setIsLoading(false);
    dispatch(checkoutSuccess());
    if (onSuccess) {
      await onSuccess();
    }
  }, [dispatch, onSuccess]);

  const renderDrawerContent = () => {
    // Prevents rendering the CheckoutDrawer component when initiatedFrom is set to null
    // when closing the drawer.
    if (!initiatedFrom) {
      return null;
    }

    const {
      title,
      subtitle,
      buttonLabel,
      success_view: SuccessView,
      subscriptionType,
      // eslint-disable-next-line max-len
      // @ts-expect-error initaitedFrom could be typed as a Hiring component, but we have built our own, so not actually possible
    } = CHECKOUT_MAPPING[initiatedFrom];

    if (status === 'success') {
      return <SuccessView />;
    }

    return (
      <Box mv={12} ph={32} flex column>
        <Box gap={12} column>
          <Text variant="heading2">{title}</Text>
          <Text variant="body" color="mono900">
            {subtitle}
          </Text>
        </Box>

        <Checkout
          price={selectedPrice}
          onSuccess={handleSuccess}
          onLoading={handleOnLoading}
          subscriptionType={subscriptionType}
          selectedProductUuid={selectedProductUuid}
        />

        <PurchaseMethodButton fullWidth variant="primary" isLoading={isLoading}>
          {buttonLabel || toI18n('checkout_drawer.confirm_and_pay')}
        </PurchaseMethodButton>
      </Box>
    );
  };

  return (
    <Drawer
      className={cx()}
      maxWidth="464px"
      open={isOpen}
      variant="temporary"
      onClose={handleClose}
    >
      <Box hright mt={16} mr={16}>
        <IconButton
          onClick={handleClose}
          icon="Close"
          size="small"
          uxElement="checkout_drawer.close_button"
          label={toI18n('checkout_drawer.close_button_labels.general')}
        />
      </Box>

      {renderDrawerContent()}
    </Drawer>
  );
};

const CheckoutDrawerWithUxRoot = () => {
  const { initiatedFrom } = useSelector(getCheckout);

  // Map initiatedFrom to appropriate product area
  const getProductArea = (initiatedFromValue: any) => {
    switch (initiatedFromValue) {
      case 'TRAINING_ASSISTANT':
        return PRODUCT_AREAS.TRAINING_ASSISTANT;
      default:
        return PRODUCT_AREAS.TRAINING_ASSISTANT; // fallback
    }
  };

  const WrappedComponent = withUxRoot({
    productArea: getProductArea(initiatedFrom),
    eventCategory: EVENT_CATEGORIES.CHECKOUT,
  })(CheckoutDrawer);

  return <WrappedComponent />;
};

export default CheckoutDrawerWithUxRoot;
